import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../shared/components/Icon';
import useEscapeKeyClose from '../../../shared/hooks/useEscapeKeyClose';
import { changePasswordApi } from '../../../api/carbonApi';
import { applyApiFieldErrors } from '../../../shared/api';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import '../../../styles/common.css';
import '../../../styles/modal.css';
import '../../../styles/forms.css';
import '../styles/changePassword.css';

export default function ChangePasswordModal({ onClose, onLogout }) {
  const { t } = useTranslation();
  const [form, setForm]       = useState({ old_password: '', new_password: '', confirm_new_password: '' });
  const [showPwd, setShowPwd] = useState({ old: false, new: false, confirm: false });
  const [error, setError]     = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [saving, setSaving]   = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEscapeKeyClose({ onClose });

  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setFieldErrors(current => ({ ...current, [field]: '' }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (form.new_password.length < 8) {
      setFieldErrors({ new_password: t('changePassword.tooShort') });
      return;
    }
    if (form.new_password !== form.confirm_new_password) {
      setFieldErrors({ confirm_new_password: t('changePassword.mismatch') });
      return;
    }

    setSaving(true);
    try {
      await changePasswordApi(form);
      setSuccess(true);
      timerRef.current = setTimeout(() => {
        if (onLogout) onLogout();
      }, 1500);
    } catch (err) {
      const nextFieldErrors = {};
      let nextRootError = '';
      const hasFieldErrors = applyApiFieldErrors(err, (field, detail) => {
        if (Object.prototype.hasOwnProperty.call(form, field)) {
          nextFieldErrors[field] = detail.message;
        } else {
          if (!nextRootError) nextRootError = detail.message;
        }
      }, t);
      if (hasFieldErrors) {
        setFieldErrors(nextFieldErrors);
        setError(nextRootError);
      } else {
        setError(resolveUserFacingError(err, t('changePassword.failed')));
      }
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-backdrop">
      <div className="modal-panel pwd-modal">
        {/* Header */}
        <div className="modal-header">
          <span className="modal-header__title">{t('changePassword.title')}</span>
          <button className="modal-header__close" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {success ? (
          <div className="modal-body">
            <div className="pwd-modal__success">
              <Icon name="check" size={18} stroke={2.5} />
              {t('common.feedback.update.success')}
            </div>
            <button className="btn-primary w-full flex-center mt-8" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-body">
            {[
              { key: 'old_password',         labelKey: 'changePassword.current', show: showPwd.old,     toggle: () => setShowPwd(s => ({ ...s, old: !s.old })) },
              { key: 'new_password',         labelKey: 'changePassword.new',     show: showPwd.new,     toggle: () => setShowPwd(s => ({ ...s, new: !s.new })) },
              { key: 'confirm_new_password', labelKey: 'changePassword.confirm', show: showPwd.confirm, toggle: () => setShowPwd(s => ({ ...s, confirm: !s.confirm })) },
            ].map(({ key, labelKey, show, toggle }) => (
              <div key={key} className="pwd-field">
                <label className="pwd-field__label">{t(labelKey)}</label>
                <div className="pwd-field__wrap">
                  <input
                    type={show ? 'text' : 'password'}
                    value={form[key]}
                    onChange={e => update(key, e.target.value)}
                    required
                    className="pwd-field__input"
                    aria-invalid={Boolean(fieldErrors[key])}
                    aria-describedby={fieldErrors[key] ? key + '-error' : undefined}
                    placeholder="••••••••"
                  />
                  <button type="button" className="pwd-field__toggle" onClick={toggle} tabIndex={-1} aria-label={t(show ? 'changePassword.hidePassword' : 'changePassword.showPassword')}>
                    <Icon name={show ? 'eye-off' : 'eye'} size={15} />
                  </button>
                </div>
                {fieldErrors[key] && (
                  <div id={key + '-error'} className="error-box" role="alert">
                    {fieldErrors[key]}
                  </div>
                )}
              </div>
            ))}

            {error && <div className="error-box">{error}</div>}

            <div className="pwd-modal__btn-row">
              <button type="button" className="btn-ghost-sm pwd-modal__btn" onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary pwd-modal__btn"
                disabled={saving}
              >
                {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : t('changePassword.update')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.querySelector('.app') || document.body
  );
}
